import { BREADCRUMB_LIMIT, sanitizeUrl, type Breadcrumb, type BreadcrumbKind } from "@brasilnovo/shared";

// =====================================================================
// Trilha de passos do usuário.
//
// Existe para responder à única pergunta que todo chamado de suporte gera:
// "o que você fez antes?". Perguntar isso a quem está no meio do turno
// devolve "não sei, eu só cliquei" — e a resposta é honesta: ninguém
// memoriza os próprios cliques. Então o portal lembra por ele.
//
// REGRA INVIOLÁVEL: nunca gravar VALOR digitado.
// O portal manipula motorista, placa, cliente e tarifa. Uma trilha que
// guardasse o conteúdo dos campos transformaria cada chamado num vazamento
// de dado operacional para dentro de um ticket que circula por Slack e
// GitHub. Gravamos o RÓTULO do campo ("Motorista") e se ele ficou
// preenchido ou vazio — que é o que reproduz o bug — nunca o que foi
// digitado. Ver docs/RELATO_DE_ERROS.md.
// =====================================================================

const CHAVE_SESSAO = "bn_trilha";

/**
 * Buffer circular. O teto vem do shared porque é o MESMO número que o
 * schema da API valida: um front que guardasse 200 passos produziria
 * relatos rejeitados com 400 na hora de enviar.
 */
let trilha: Breadcrumb[] = [];

/** Restaura a trilha da sessão — uma tela branca costuma terminar em reload,
 *  e é justamente aí que os passos anteriores valem mais. */
function restaurar(): void {
  try {
    const bruto = sessionStorage.getItem(CHAVE_SESSAO);
    if (bruto) trilha = (JSON.parse(bruto) as Breadcrumb[]).slice(-BREADCRUMB_LIMIT);
  } catch {
    trilha = []; // sessionStorage indisponível ou corrompido: seguir sem trilha
  }
}

function persistir(): void {
  try {
    sessionStorage.setItem(CHAVE_SESSAO, JSON.stringify(trilha));
  } catch {
    /* cota estourada ou modo privado: a trilha em memória continua valendo */
  }
}

export function record(kind: BreadcrumbKind, label: string, extra?: { detail?: string; ok?: boolean }): void {
  trilha.push({
    t: Date.now(),
    kind,
    label: label.slice(0, 300),
    detail: extra?.detail?.slice(0, 2000),
    ok: extra?.ok,
  });
  if (trilha.length > BREADCRUMB_LIMIT) trilha = trilha.slice(-BREADCRUMB_LIMIT);
  persistir();
}

/** Cópia da trilha, para envio. */
export const trail = (): Breadcrumb[] => [...trilha];

export function clearTrail(): void {
  trilha = [];
  persistir();
}

// ---------------------------------------------------------------------
// Rotulagem de elementos
// ---------------------------------------------------------------------

const limpar = (s: string | null | undefined): string =>
  (s ?? "").replace(/\s+/g, " ").trim().slice(0, 60);

/** Nome legível de um controle — o que a PESSOA vê, não o seletor CSS. */
function rotuloDoControle(el: Element): string {
  const aria = limpar(el.getAttribute("aria-label"));
  if (aria) return aria;
  const titulo = limpar(el.getAttribute("title"));
  if (titulo) return titulo;
  const texto = limpar((el as HTMLElement).innerText || el.textContent);
  if (texto) return texto;
  const teste = limpar(el.getAttribute("data-testid"));
  if (teste) return teste;
  return limpar(el.getAttribute("name")) || el.tagName.toLowerCase();
}

/**
 * Rótulo de um campo de formulário.
 *
 * O primeiro palpite tem de ser o `<label>` IRMÃO ANTERIOR: neste portal os
 * rótulos não têm `htmlFor`/`id` (o mesmo detalhe que obrigou os testes E2E
 * a criarem o helper `fieldByLabel`). Confiar só em `labels`/`htmlFor`
 * devolveria "input" para praticamente todo campo da aplicação.
 */
function rotuloDoCampo(el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string {
  const irmao = el.previousElementSibling;
  if (irmao?.tagName === "LABEL") {
    const t = limpar((irmao as HTMLElement).innerText);
    if (t) return t;
  }
  const associado = (el as HTMLInputElement).labels?.[0];
  if (associado) {
    const t = limpar(associado.innerText);
    if (t) return t;
  }
  return (
    limpar(el.getAttribute("aria-label")) ||
    limpar(el.getAttribute("placeholder")) ||
    limpar(el.getAttribute("name")) ||
    "campo"
  );
}

/** Nome da tela a partir do caminho — "/cadastros/frota" → "Cadastros › frota". */
export function nomeDaTela(pathname: string): string {
  const partes = pathname.split("/").filter(Boolean);
  if (!partes.length) return "Dashboard";
  return partes.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" › ");
}

// ---------------------------------------------------------------------
// Instrumentação automática
// ---------------------------------------------------------------------

let instalado = false;

/**
 * Liga os ouvintes globais. Chamado uma única vez no boot.
 *
 * Tudo em fase de CAPTURA (`true` no addEventListener): um `stopPropagation`
 * em qualquer handler da aplicação — comum em modais e menus — faria o
 * evento nunca chegar ao document na fase de bolha, e os cliques que mais
 * interessam (os que abrem e fecham coisas) sumiriam da trilha.
 */
export function installBreadcrumbs(): void {
  if (instalado) return;
  instalado = true;
  restaurar();

  document.addEventListener(
    "click",
    (ev) => {
      const alvo = ev.target as Element | null;
      const acionavel = alvo?.closest?.("button, a, [role='button'], summary, [data-bn-track]");
      if (!acionavel) return;
      // O próprio botão de relatar não entra na trilha: ele é o fim da
      // história, não um passo dela.
      if (acionavel.closest("[data-bn-noshot]")) return;
      const tag = acionavel.tagName === "A" ? "Abriu" : "Clicou em";
      record("clique", `${tag} «${rotuloDoControle(acionavel)}»`);
    },
    true,
  );

  document.addEventListener(
    "change",
    (ev) => {
      const el = ev.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
      if (!el || !/^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) return;
      if (el.closest("[data-bn-noshot]")) return;

      const rotulo = rotuloDoCampo(el);
      // Caixas de seleção: marcar/desmarcar é o passo, e não é dado sensível.
      if (el instanceof HTMLInputElement && (el.type === "checkbox" || el.type === "radio")) {
        record("campo", `${el.checked ? "Marcou" : "Desmarcou"} «${rotulo}»`);
        return;
      }
      // Demais campos: só o FATO de ter sido preenchido. Ver a regra no topo.
      record("campo", `${el.value ? "Preencheu" : "Limpou"} «${rotulo}»`);
    },
    true,
  );

  window.addEventListener("error", (ev) => {
    record("erro", ev.message || "Erro de JavaScript", {
      detail: [ev.filename && `${ev.filename}:${ev.lineno}:${ev.colno}`, ev.error?.stack].filter(Boolean).join("\n"),
      ok: false,
    });
  });

  window.addEventListener("unhandledrejection", (ev) => {
    const r = ev.reason as { message?: string; stack?: string } | string | undefined;
    record("erro", typeof r === "string" ? r : (r?.message ?? "Promessa rejeitada sem tratamento"), {
      detail: typeof r === "object" ? r?.stack : undefined,
      ok: false,
    });
  });

  // console.error é onde React deposita avisos de renderização quebrada —
  // frequentemente a única pista de uma tela que "só ficou em branco".
  const originalErro = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    const texto = args
      .map((a) => (a instanceof Error ? `${a.message}\n${a.stack ?? ""}` : typeof a === "string" ? a : safeJson(a)))
      .join(" ")
      .slice(0, 600);
    // Ignora o ruído do próprio React em modo dev sobre chaves de lista.
    if (texto && !/Warning: .*key/i.test(texto)) record("console", "console.error", { detail: texto, ok: false });
    originalErro(...args);
  };
}

const safeJson = (v: unknown): string => {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
};

// ---------------------------------------------------------------------
// Registro de chamadas de API (usado pelos interceptors do axios)
// ---------------------------------------------------------------------

export function recordApi(method: string, url: string, status: number, ms: number, erro?: string): void {
  const ok = status >= 200 && status < 400;
  record("api", `${method.toUpperCase()} ${sanitizeUrl(url)}`, {
    detail: `HTTP ${status || "sem resposta"} · ${Math.round(ms)}ms${erro ? ` — ${erro}` : ""}`,
    ok,
  });
}

/** Passo de navegação. Chamado pelo rastreador de rotas do React Router. */
export function recordNavigation(pathname: string): void {
  const ultimo = trilha[trilha.length - 1];
  // Evita duplicar quando o router re-renderiza na mesma rota (StrictMode
  // monta o efeito duas vezes em desenvolvimento).
  if (ultimo?.kind === "navegacao" && ultimo.label.endsWith(`«${pathname}»`)) return;
  record("navegacao", `Abriu ${nomeDaTela(pathname)} «${pathname}»`);
}
